import introPoster from '#src/app/pages/ambassadors/landing/shared/assets/intro-walkthrough-poster.jpg';

/**
 * Pathname of the intro video on `static.change.org`, resolved to a full per-environment URL
 * at runtime by `useVideoWalkthrough` via `useGetStaticAssetUrl`. Deliberately a pathname and
 * not a URL — never hardcode the `static.change.org` host (enforced by the
 * `@change-corgi/no-static-asset-url` lint rule).
 *
 * The upload happens out-of-band — this repo only references `static.change.org`, it does not
 * publish to it. The file goes in the `change-assets` S3 bucket with public-read ACL; see the
 * "Adding Static Assets to Corgi" runbook linked from docs/front-end/index.md.
 *
 * DEPLOY ORDER: the object must exist in S3 before this ships, or the play button is inert.
 * Set back to '' to render poster-only if this ever needs to merge ahead of an upload. As of
 * this commit the object is live on both the staging and production origins.
 *
 * The uploaded file is faststart (moov atom at the front), which is the property that matters:
 * without it the browser must fetch all 32MB before it can seek or even report duration.
 *
 * Two known compromises, neither breaking:
 *   - The filename carries no version. S3 objects have no content hash, so replacing this in
 *     place risks the CDN serving a stale copy. Prefer uploading a new name over overwriting.
 *   - Keyframes sit on the encoder's default ~8.33s grid rather than being forced onto the
 *     `QUESTION_TIMESTAMPS_SECONDS` values, so a chapter jump decodes from up to ~7.7s before
 *     its target (worst case Q4 at 91s, whose nearest preceding keyframe is 83.33s). Seeks are
 *     correct, just slower to paint on a slow connection. Re-cutting with
 *     `-force_key_frames 0,18,43,91,204,273` removes that at ~+1.7% filesize.
 */
export const VIDEO_ASSET_PATHNAME = '/ambassadors/intro.mp4';

/**
 * Poster frame, bundled rather than served from `static.change.org`: it's small (63 kB, well
 * under the 128 kB jpg cap enforced by `apps/front-end/scripts/check-assets.sh`) and only ever
 * changes when a dev swaps the video. Grabbed from t=0.8s of the source, so it matches the
 * video's opening frame and the handoff into playback is seamless.
 *
 * If the video is ever replaced on S3 without a corresponding release, this will go stale —
 * regenerate it alongside the new cut.
 */
export const POSTER_SRC = introPoster;

/**
 * Runtime of the video, used only to render the duration pill. The real asset is 293.7s;
 * 294 is used so `formatDurationLabel` renders "4:54" rather than flooring to "4:53".
 */
export const VIDEO_DURATION_SECONDS = 294; // 4:54

export type QuestionNumber = 1 | 2 | 3 | 4 | 5;

/**
 * Verified against the real cut by sampling frames at each mark: Q1 lands on "about the
 * program", Q3 on the "Picking Your Petition Topic" slide, Q4 on "Your Deliverables", Q5 on
 * "Your Timeline". Note Q3 (204s) sits *after* Q4 (91s) — the video covers share deliverables
 * before topic selection, so the numbered list is not in chronological order. That's the
 * content's ordering, not a mistake in these values.
 */
export const QUESTION_TIMESTAMPS_SECONDS: Readonly<Record<QuestionNumber, number>> = {
	1: 18,
	2: 43,
	3: 204,
	4: 91,
	5: 273,
};

export const QUESTION_NUMBERS: readonly QuestionNumber[] = [1, 2, 3, 4, 5];

export function formatDurationLabel(totalSeconds: number): string {
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = Math.floor(totalSeconds % 60);
	return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
